import { Select, SelectProps, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PaginatedResponse, PaginatedResponseData } from '../../services/api/client';
import { BaseOptionType, DefaultOptionType } from 'rc-select/lib/Select';
import { debounce } from 'lodash';
import collect from 'collect.js';
import PaginationHelpers from '../../services/utils/pagination/PaginationHelpers';

type Props<Type, ValueType, OptionType extends BaseOptionType = DefaultOptionType> = SelectProps<
  ValueType,
  OptionType
> & {
  debounceTimeout?: number;
  loadPage: (page: number, query?: string) => PaginatedResponse<Type>;
  castOption?: (item: Type) => OptionType;
  findByValue?: (value: ValueType) => Type;
  onChange?: (value: ValueType | ValueType[], object: Type | Type[]) => void;
};

export default function PaginatedSelect<
  Type,
  ValueType,
  OptionType extends BaseOptionType = DefaultOptionType,
>({
  loadPage,
  castOption,
  debounceTimeout = 500,
  findByValue,
  onChange,
  ...props
}: Props<Type, ValueType, OptionType>) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponseData<Type>>(
    PaginationHelpers.initPaginationData(),
  );

  useEffect(() => {
    _loadPage(1);
  }, []);

  async function _loadPage(page: number, query?: string) {
    return loadPage(page, query)
      .then((response) =>
        setData({
          data: [...data.data, ...response.data.data],
          meta: response.data.meta,
        }),
      )
      .catch((error) => console.error('Failed to load data', { error }))
      .finally(() => setLoading(false));
  }

  const debouncedLoader = useMemo(() => {
    const loadOptions = (value: string) => {
      setData(PaginationHelpers.initPaginationData());
      setLoading(true);

      loadPage(1, value)
        .then(({ data: _data }) => setData(_data))
        .catch((error) => console.error('Failed to load data', { error }))
        .finally(() => setLoading(false));
    };

    return debounce(loadOptions, debounceTimeout);
  }, [loadPage, debounceTimeout]);

  const onPageEnd = useCallback(() => {
    if (data.meta.current_page >= data.meta.last_page) {
      return;
    }

    _loadPage(data.meta.current_page + 1);
  }, [data.meta]);

  const _onChange = onChange
    ? (value: ValueType | ValueType[]) => {
        const _findByValue =
          findByValue ??
          ((_value: ValueType | ValueType[]) => collect(data.data).firstWhere('id', _value));

        onChange(value, _findByValue(value));
      }
    : undefined;

  const options = useMemo<OptionType[]>(() => {
    const caster = castOption ?? ((item: any) => ({ label: item.name, value: item.id }));

    return data.data.map((item) => caster(item)) as OptionType[];
  }, [data.data, castOption]);

  return (
    <Select
      {...props}
      options={options}
      showSearch
      filterOption={false}
      onChange={_onChange}
      onSearch={debouncedLoader}
      notFoundContent={loading ? <Spin size="small" /> : null}
      onPopupScroll={(e) => {
        e.persist();
        const target = e.target as HTMLDivElement;

        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
          onPageEnd();
        }
      }}
    />
  );
}
